import * as React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import { ButtonGroup } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import RecyclingIcon from "@mui/icons-material/Recycling";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddBoxIcon from "@mui/icons-material/AddBox";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FilterListIcon from "@mui/icons-material/FilterList";
import { visuallyHidden } from "@mui/utils";
import { deleteMultiple } from "../customHooks/FetchDataHook";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import Fade from "@mui/material/Fade";
import Zoom from "@mui/material/Zoom";
import CustomizedTooltip from "../utils/CustomizedTooltip";
import { styled, alpha } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import ErrorMessage from "../ErrorMessage/ErrorMessage";
import { pages } from "../NavigationBar/AppBarResponsive";
import DownloadCSV from "../utils/DownloadCSV/DownloadCSV";
import DownloadJSON from "../utils/DownloadCSV/DownloadJSON";
import PrintIcon from "@mui/icons-material/Print";
import Button from "@mui/material/Button";
import { printTable } from "./printTable";
import {
  spanishFormat,
  roundCents,
  currencyFormat,
} from "../utils/generalData/numbersAndCurrencies";
import {
  procesarFechaISO,
  getDateAndTime,
  handleDate,
} from "../utils/generalData/fechaDiccionario";

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// Since 2020 all major browsers ensure sort stability with Array.prototype.sort().
// stableSort() brings sort stability to non-modern browsers (notably IE11). If you
// only support modern browsers you can replace stableSort(exampleArray, exampleComparator)
// with exampleArray.slice().sort(exampleComparator)
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

// Detecta fechas tipo 2024-09-25T00:55:56.243Z
const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;

export function EnhancedTableHead(props) {
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
  } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            color="info"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
          />
        </TableCell>
        {props.headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            variant="head"
            align={headCell.numeric ? "right" : "left"}
            sx={{
              verticalAlign: "top",
              fontWeight: 800,
              textTransform: "uppercase",
            }}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

export const StyledTooltip = styled(({ className, ...props }) => (
  <Tooltip
    {...props}
    classes={{ popper: className }}
    slots={{
      transition: Zoom,
    }}
    slotProps={{
      transition: { timeout: 600 },
    }}
  />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    background: "#efe",
    //"linear-gradient(0deg,rgba(41, 125, 133, 1) 0%, rgba(89, 227, 234, 1) 100%)",
    color: "#000",
    border: "2px solid #efe",
    borderRadius: 3,
    boxShadow: "10px 10px 7px #00000033",
    padding: "10px",
    fontSize: 13,
    textTransform: "uppercase",
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: "#efe",
  },
}));

export const DangerTooltip = styled(({ className, ...props }) => (
  <Tooltip
    {...props}
    classes={{ popper: className }}
    slots={{
      transition: Zoom,
    }}
    slotProps={{
      transition: { timeout: 600 },
    }}
  />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    background:
      "linear-gradient(0deg,rgba(170, 34, 34, 1) 0%, rgba(255, 68, 68, 1) 100%)",
    color: "#fff",
    boxShadow: "7px 10px 10px #00000077",
    padding: "10px",
    fontSize: 13,
    border: "2px solid #a22",
    borderRadius: 8,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: "#a22",
  },
}));

/**
 * Genera un gradiente a partir de un color base de MUI
 */
const makeGradient = (color) =>
  `linear-gradient(
    0deg,
    ${alpha(color, 0.85)} 0%,
    ${alpha(color, 0.55)} 100%
  )`;

export const GradientTooltip = styled(
  ({ className, color = "primary", ...props }) => (
    <Tooltip
      {...props}
      arrow
      classes={{ popper: className }}
      slots={{
        transition: Zoom,
      }}
      slotProps={{
        transition: { timeout: 600 },
      }}
    />
  ),
)(({ theme, color }) => {
  const paletteColor = theme.palette[color] || theme.palette.primary;

  return {
    [`& .${tooltipClasses.tooltip}`]: {
      background: "#efe", //makeGradient("#efe"),
      color: paletteColor.dark,
      border: `1px solid ${paletteColor.main}`,
      borderRadius: 3,
      boxShadow: "8px 10px 10px rgba(0,0,0,0.35)",
      padding: "10px",
      fontSize: 13,
      textTransform: "uppercase",
    },
    [`& .${tooltipClasses.arrow}`]: {
      color: paletteColor.main,
    },
  };
});

export function EnhancedTableToolbar(props) {
  const { numSelected } = props;
  const trashMode = props.collection.includes("/trash");
  const navigate = useNavigate();
  const [useError, setError] = React.useState(null);

  return useError ? (
    <ErrorMessage message={useError} title={"Error en tabla de datos"} />
  ) : (
    <Toolbar>
      {numSelected > 0 ? (
        <Typography
          sx={{ flex: "1 1 100%" }}
          color="inherite"
          variant="subtitle"
          component="div"
        >
          {numSelected} seleccionado
        </Typography>
      ) : (
        <Typography
          sx={{ flex: "1 1 100%" }}
          variant="h6"
          id="tableTitle"
          component="div"
        >
          {props.title}
        </Typography>
      )}
      {!trashMode && (
        <GradientTooltip title="Agregar" color="primary" arrow>
          <IconButton
            onClick={() => {
              navigate(`/${props.collection.replace("/urg", "")}/add`);
            }}
            sx={{ alignSelf: "right" }}
          >
            <AddBoxIcon color="primary" />
          </IconButton>
        </GradientTooltip>
      )}

      {numSelected > 0 ? (
        <>
          {numSelected === 1 && !trashMode && (
            <>
              <GradientTooltip title="Copiar" arrow color="secondary">
                <IconButton
                  onClick={() => {
                    navigate(
                      `/${props.collection.replace("/urg", "")}/copy/${
                        props.idSelected
                      }`,
                    );
                  }}
                  sx={{ alignSelf: "right" }}
                >
                  <ContentCopyIcon color="secondary" />
                </IconButton>
              </GradientTooltip>
              <GradientTooltip title="Editar" arrow color="info">
                <IconButton
                  onClick={() => {
                    navigate(
                      `/${props.collection.replace("/urg", "")}/edit/${
                        props.idSelected
                      }`,
                    );
                  }}
                  sx={{ alignSelf: "right" }}
                >
                  <EditIcon color="info" />
                </IconButton>
              </GradientTooltip>
            </>
          )}

          <GradientTooltip
            title={trashMode ? "Restaurar" : "¡BORRAR!"}
            color={trashMode ? "success" : "error"}
            arrow
          >
            <IconButton
              sx={{ alignSelf: "right" }}
              onClick={() => {
                const pasarBorrado = () => {
                  try {
                    props.deleted([props.idSelected]);
                    deleteMultiple(
                      props.idSelected,
                      props.collection.replace("/urg", ""),
                    );
                    props.resetSelected([]);
                  } catch (e) {
                    setError(e);
                  }
                };

                pasarBorrado();
              }}
              color={!trashMode ? "error" : "success"}
            >
              {!trashMode && <DeleteIcon />}
              {trashMode && <RecyclingIcon />}
            </IconButton>
          </GradientTooltip>
          {!trashMode && (
            <GradientTooltip
              title="Papelera de reciclaje"
              color="success"
              arrow
            >
              <IconButton
                onClick={() => {
                  navigate(`/${props.collection.replace("/urg", "")}/trash`);
                }}
                sx={{ alignSelf: "right" }}
              >
                <RestoreFromTrashIcon color="success" />
              </IconButton>
            </GradientTooltip>
          )}
        </>
      ) : (
        <IconButton
          disabled={trashMode}
          onClick={() => {
            navigate(`/${props.collection.replace("/urg", "")}/trash`);
          }}
          sx={{ alignSelf: "right" }}
        >
          <RestoreFromTrashIcon color={!trashMode ? "success" : "default"} />
        </IconButton>
      )}
    </Toolbar>
  );
}

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
};

export default function EnhancedTable(props) {
  const [order, setOrder] = React.useState("desc");
  const [orderBy, setOrderBy] = React.useState("Fecha");
  const [selected, setSelected] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [dense, setDense] = React.useState(true);
  const [error, setError] = React.useState(false);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const rows = props.rows;
  const navigate = useNavigate();

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = rows.map((n) => n._id);
      setSelected(newSelected);
      props.editor(newSelected);
      return;
    }
    setSelected([]);
    props.editor([]);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
    props.editor(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeDense = (event) => {
    setDense(event.target.checked);
  };

  // helper: detecta campos tipo id (_id, userId, tenantId, etc.)
  const isIdField = (key) => typeof key === "string" && /id$/i.test(key);

  // head cells visibles (sin columnas id)
  const visibleHeadCells = (props.headCells || []).filter(
    (h) => !isIdField(h.id),
  );

  // This function sums all numeric values in columns that are numbers and counts the number of items if they are strings.
  // then add this row to the end of the table.
  const sumColumns = (rows) => {
    const sums = {};
    rows.forEach((row) => {
      Object.entries(row).forEach(([key, value]) => {
        // ignorar keys que son ids
        if (isIdField(key) || key === "_id") return;
        if (typeof value === "number") {
          sums[key] = (sums[key] || 0) + value;
        } else if (typeof value === "string") {
          sums[key] = (sums[key] || 0) + 1; // Count strings
        }
      });
    });
    // add sums to the end of the table
    sums._id = "Total"; // Add an identifier for the sum row
    rows.push(sums);
  };

  if (rows[rows.length - 1]?._id !== "Total") {
    sumColumns(rows);
  }

  const isSelected = (name) => selected.indexOf(name) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const visibleRows = React.useMemo(
    () =>
      stableSort(rows, getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      ),
    [order, orderBy, page, rowsPerPage, rows],
  );

  let i = 0;

  const failure = <ErrorMessage message={error} />;

  const tableTitle =
    pages.find((pg) => pg.path === props.collection.replace("/urg", ""))
      ?.text || props.collection;

  const filtersString = (arr)=> {
  if (!Array.isArray(arr)) {
    return "El parametro debe ser un Array"
  }
  let str = ""
    arr.map((e, index)=>{
      console.log(e)
      if(index === 0) {
        str = e.column + ": " + e.query
        console.log("El primer elemento: " + str)
      } else if (index <= arr.length) {
        console.log("index menor o igual que el array")
        str = str + ", " + e.column + ": " + e.query
        console.log(str)
      }
    })
    console.log(str)
    return str
  };

const filterList = filtersString(props.filters)

  const success = (
    <>
      <EnhancedTableToolbar
        collection={props.collection.replace("/urg", "")}
        title={tableTitle}
        numSelected={selected.length}
        idSelected={selected}
        deleted={props.deleted}
        resetSelected={setSelected}
      />
      <TableContainer>
        <Table
          sx={{ minWidth: 750 }}
          aria-labelledby="tableTitle"
          size={dense ? "small" : "medium"}
        >
          <EnhancedTableHead
            numSelected={selected.length}
            order={order}
            orderBy={orderBy}
            onSelectAllClick={handleSelectAllClick}
            onRequestSort={handleRequestSort}
            rowCount={rows.length}
            headCells={visibleHeadCells}
          />
          <TableBody>
            {visibleRows.map((row, index) => {
              const isItemSelected = isSelected(row._id);
              const labelId = `enhanced-table-checkbox-${index}`;

              return (
                <TableRow
                  hover
                  onClick={(event) => handleClick(event, row._id)}
                  key={row._id + index}
                  onDoubleClick={() => {
                    navigate(
                      `/${props.collection.replace("/urg", "")}/edit/${row._id}`,
                    );
                  }}
                  role="checkbox"
                  aria-checked={isItemSelected}
                  tabIndex={-1}
                  selected={isItemSelected}
                  sx={{
                    cursor: "pointer",
                    backgroundColor: index % 2 ? "#00000016" : "#ffffff00",
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="info"
                      checked={isItemSelected}
                      inputProps={{
                        "aria-labelledby": labelId,
                      }}
                    />
                  </TableCell>

                  {visibleHeadCells.map((headCell) => {
                    const element = row[headCell.id];
                    const key = `${row._id}-${headCell.id}-${index}`;
                    if (typeof element === "object") {
                      return (
                        <TableCell
                          key={key}
                          align={headCell.numeric ? "right" : "left"}
                        >
                          <Typography variant="body1" noWrap>
                            N/A
                          </Typography>
                        </TableCell>
                      );
                    } else {
                      return (
                        <TableCell
                          align={headCell.numeric ? "right" : "left"}
                          key={key}
                        >
                          {typeof element === "number"
                            ? spanishFormat(element)
                            : isoDateRegex.test(element)
                              ? handleDate(element)
                              : element}
                        </TableCell>
                      );
                    }
                  })}
                </TableRow>
              );
            })}
            {emptyRows > 0 && (
              <TableRow
                style={{
                  height: (dense ? 33 : 53) * emptyRows,
                }}
              >
                <TableCell colSpan={6} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 15, 20, rows.length]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        showFirstButton={true}
        showLastButton={true}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <ButtonGroup>
        <DownloadCSV
          head={props.headCells.map((h) => h.label)}
          data={props.rows}
          fileName={`${props.collection}`}
        />
        <DownloadJSON data={props.rows} fileName={`${props.collection}`} />
        <StyledTooltip title="Imprimir" arrow>
        <IconButton
          color="primary"
          onClick={() =>
            printTable({
              rows,
              headCells: visibleHeadCells,
              title: `${tableTitle}`,
              subtitle: `${filterList}`,
            })
          }
        >
          <PrintIcon />
        </IconButton>
      </StyledTooltip>
      </ButtonGroup>

      <FormControlLabel
        control={<Switch checked={dense} onChange={handleChangeDense} />}
        label="Dense padding"
      />
    </>
  );

  return error ? failure : success;
}
